import { RequestFormExtended, ApplicationForm, BiospecimenRequest, UserQuery } from '../scripts/request-form.objects';
import { PickListItem, JsonItem } from '../../../../shared/components/web-form/web-form.objects';
import { BiospecimenInventoryLookup } from '../../inventory-summary/scripts/inventory-lookup';
import { VisitLookup } from '../../../../secure/reporting/data-completeness/components/visits-tab/scripts/visits.lookup';
import { FormatDate } from '../../../../shared/scripts/date-functions';
import { DocumentInfoV2 } from '../../../../shared/services/documents/document.objects';
import _ from 'lodash';

export class RequestFormView {
    viewId: string;
    viewType: string;
    isReviewer: boolean;

    baseRequest: RequestFormExtended;
    institutionTypePickList: PickListItem[];
    supportPickList: PickListItem[];
    yesNoPickList: PickListItem[];
    fundingStatusPickList: PickListItem[];
    requestTypePickList: PickListItem[];
    queryList: UserQuery[];

    RequestorName: string; //First Name, Last Name, Email
    InstitutionName: string;
    InstitutionType: string;
    RequestorTelephone: string;

    LetterAddressedTo: string;
    LetterNeededByDate: string;

    ProjectTitle: string;
    ShortTitle: string;
    PINames: string;
    CollaboratingInvestigators: string;
    ExpertiseNeeded: string;
    Background: string;
    ScientificAims: string;
    Hypothesis: string;
    RationaleImpact: string;
    Methods: string;
    SampleSizePowerAnalysisApproach: string;
    ParticipantDescription: string;
    AssayDescription: string;

    BiostatisticianSupport: string;
    RelevantLiteratureReferences: string;

    SharingPlan: string;
    BiospecimenUsageAbility: string;
    StartDate: string;
    CompletionDate: string;
    HasExternalCollaborators: string;
    ExternalCollaboratorsComment: string;
    FundingStatus: string;
    FundingSources: string;

    HasPreviousRequest: string;
    NewOrResubmittedRequest: string;

    EngagementDescription: string;

    BiospecimenRequest: BiospecimenRequestView;

    constructor(viewId: string, viewType: string = "", isReviewer: boolean = false) {
        this.viewId = viewId;
        this.viewType = viewType;
        this.isReviewer = isReviewer;
    }

    initialize(baseRequest: RequestFormExtended, institutionTypePickList: PickListItem[], supportPickList: PickListItem[], yesNoPickList: PickListItem[], fundingStatusPickList: PickListItem[], requestTypePickList: PickListItem[], queryList: UserQuery[]) {
        this.baseRequest = baseRequest;
        this.institutionTypePickList = institutionTypePickList;
        this.supportPickList = supportPickList;
        this.yesNoPickList = yesNoPickList;
        this.fundingStatusPickList = fundingStatusPickList;
        this.requestTypePickList = requestTypePickList;
        this.queryList = queryList;

        //Requester Info:
        let requestorNameArr = [this.baseRequest.ApplicationForm.RequestorFirstName, this.baseRequest.ApplicationForm.RequestorLastName]
        if (this.baseRequest.ApplicationForm.RequestorEmail) requestorNameArr.push(`(${this.baseRequest.ApplicationForm.RequestorEmail})`);
        this.RequestorName = requestorNameArr.join(" ");

        this.RequestorTelephone = this.baseRequest.ApplicationForm.RequestorTelephone;

        //Institution Name and Type:
        this.InstitutionName = this.baseRequest.ApplicationForm.InstitutionName;
        this.InstitutionType = "";
        if (this.baseRequest.ApplicationForm.InstitutionTypeId > 0) {
            let foundType = this.institutionTypePickList.find(i => i.Id == this.baseRequest.ApplicationForm.InstitutionTypeId);
            if (foundType) {
                this.InstitutionType = foundType.Value;
            }
        }
        else if (this.baseRequest.ApplicationForm.InstitutionTypeId == -99) {
            this.InstitutionType = this.baseRequest.ApplicationForm.InstitutionTypeOther;
        }

        //Letter of Support Details:
        this.LetterAddressedTo = this.baseRequest.ApplicationForm.LetterAddressedTo;
        this.LetterNeededByDate = FormatDate(this.baseRequest.ApplicationForm.LetterNeededByDate);

        //Project Details:
        this.ProjectTitle = this.baseRequest.ApplicationForm.ProjectTitle;
        this.ShortTitle = this.baseRequest.ApplicationForm.ProjectShortTitle;
        this.PINames = this.baseRequest.ApplicationForm.PINames.split("|").join("<br>");
        this.CollaboratingInvestigators = this.baseRequest.ApplicationForm.CollaboratingInvestigators;
        this.ExpertiseNeeded = this.baseRequest.ApplicationForm.ExpertiseNeeded;
        this.Background = this.baseRequest.ApplicationForm.Background;
        this.ScientificAims = this.baseRequest.ApplicationForm.ScientificAims;
        this.Hypothesis = this.baseRequest.ApplicationForm.Hypothesis;
        this.RationaleImpact = this.baseRequest.ApplicationForm.RationaleImpact;
        this.Methods = this.baseRequest.ApplicationForm.Methods;
        this.SampleSizePowerAnalysisApproach = this.baseRequest.ApplicationForm.SampleSizePowerAnalysisApproach;
        this.ParticipantDescription = this.baseRequest.ApplicationForm.ParticipantDescription;
        this.AssayDescription = this.baseRequest.ApplicationForm.AssayDescription;

        //Biostatistician Support:
        this.BiostatisticianSupport = "";
        if (this.baseRequest.ApplicationForm.BiostatisticianSupportId > 0) {
            let foundsupportId = this.supportPickList.find(i => i.Id == this.baseRequest.ApplicationForm.BiostatisticianSupportId);
            if (foundsupportId) {
                this.BiostatisticianSupport = foundsupportId.Value;
            }
        }
        //else if (this.baseRequest.ApplicationForm.BiostatisticianSupportId == -99) {
        //    this.BiostatisticianSupport = this.baseRequest.ApplicationForm.BiostatisticianSupportOther;
        //}
        this.RelevantLiteratureReferences = this.baseRequest.ApplicationForm.RelevantLiteratureReferences;

        //Admin Details:
        this.SharingPlan = this.baseRequest.ApplicationForm.SharingPlan;
        this.BiospecimenUsageAbility = this.baseRequest.ApplicationForm.BiospecimenUsageAbility;
        this.StartDate = FormatDate(this.baseRequest.ApplicationForm.StartDate);
        this.CompletionDate = FormatDate(this.baseRequest.ApplicationForm.CompletionDate);

        this.HasExternalCollaborators = "";
        this.ExternalCollaboratorsComment = "";
        if (this.baseRequest.ApplicationForm.HasExternalCollaboratorsId > 0) {
            let foundHasExtCollab = this.yesNoPickList.find(i => i.Id == this.baseRequest.ApplicationForm.HasExternalCollaboratorsId);
            if (foundHasExtCollab) {
                this.HasExternalCollaborators = foundHasExtCollab.Value;
                if (foundHasExtCollab.Id === 1) {
                    this.ExternalCollaboratorsComment = this.baseRequest.ApplicationForm.ExternalCollaboratorsComment || "";
                }
            }
        }
        this.FundingStatus = "";
        if (this.baseRequest.ApplicationForm.FundingStatusId > 0) {
            let foundFundingStatus = this.fundingStatusPickList.find(i => i.Id == this.baseRequest.ApplicationForm.FundingStatusId);
            if (foundFundingStatus) {
                this.FundingStatus = foundFundingStatus.Value;
            }
        }
        this.FundingSources = this.baseRequest.ApplicationForm.FundingSources;

        //Request History:
        this.HasPreviousRequest = "";
        if (this.baseRequest.ApplicationForm.HasPreviousRequestId > 0) {
            let foundHasPrevReq = this.yesNoPickList.find(i => i.Id == this.baseRequest.ApplicationForm.HasPreviousRequestId);
            if (foundHasPrevReq) {
                this.HasPreviousRequest = foundHasPrevReq.Value;
            }
        }
        this.NewOrResubmittedRequest = "";
        if (this.baseRequest.ApplicationForm.NewOrResubmittedRequestId > 0) {
            let foundNewOrResubReq = this.requestTypePickList.find(i => i.Id == this.baseRequest.ApplicationForm.NewOrResubmittedRequestId);
            if (foundNewOrResubReq) {
                this.NewOrResubmittedRequest = foundNewOrResubReq.Value;
            }
        }

        //Engagement:
        this.EngagementDescription = this.baseRequest.ApplicationForm.EngagementPlans;

        let html = this.generateHtml();
        $(`#${this.viewId}`).empty().html(html);

        this.BiospecimenRequest = new BiospecimenRequestView("view-biospecimen-requests", this.isReviewer);
        this.BiospecimenRequest.initialize(this.baseRequest, this.queryList);
    }

    private generateHtml(): string {
        let contactInformationBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Requestor Contact Information
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Requestor Name
                    </div>
                    <div class="col view-application-value-col">
                        ${this.RequestorName}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Institution
                    </div>
                    <div class="col view-application-value-col">
                        ${this.InstitutionName}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Institution Type
                    </div>
                    <div class="col view-application-value-col">
                        ${this.InstitutionType}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Applicant Telephone
                    </div>
                    <div class="col view-application-value-col">
                        ${this.RequestorTelephone}
                    </div>
                </div>
            </div>    
        `;

        let letterOfSupportBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Letter of Support Details
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Letter Addressed To
                    </div>
                    <div class="col view-application-value-col">
                        ${this.LetterAddressedTo}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Letter Needed By Date
                    </div>
                    <div class="col view-application-value-col">
                        ${this.LetterNeededByDate}
                    </div>
                </div>
            </div>
        `;

        let projectDetailsBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Project Details
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Project Title
                    </div>
                    <div class="col view-application-value-col">
                        ${this.ProjectTitle}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Short Title
                    </div>
                    <div class="col view-application-value-col">
                        ${this.ShortTitle}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        PI Names
                    </div>
                    <div class="col view-application-value-col">
                        ${this.PINames}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Collaborating RECOVER Investigators
                    </div>
                    <div class="col view-application-value-col">
                        ${this.CollaboratingInvestigators}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Relevant Areas of Expertise Needed
                    </div>
                    <div class="col view-application-value-col">
                        ${this.ExpertiseNeeded}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Background
                    </div>
                    <div class="col view-application-value-col">
                        ${this.Background}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Scientific Aims
                    </div>
                    <div class="col view-application-value-col">
                        ${this.ScientificAims}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Hypothesis
                    </div>
                    <div class="col view-application-value-col">
                        ${this.Hypothesis}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Scientific Rationale and Impact
                    </div>
                    <div class="col view-application-value-col">
                        ${this.RationaleImpact}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Methods/Approach
                    </div>
                    <div class="col view-application-value-col">
                        ${this.Methods}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Sample Size, Power and Analysis Approach
                    </div>
                    <div class="col view-application-value-col">
                        ${this.SampleSizePowerAnalysisApproach}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Participant Population Description/Phenotype
                    </div>
                    <div class="col view-application-value-col">
                        ${this.ParticipantDescription}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Biostatistician Support  
                    </div>
                    <div class="col view-application-value-col">
                        ${this.BiostatisticianSupport}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Relevant Literature References
                    </div>
                    <div class="col view-application-value-col">
                        ${this.RelevantLiteratureReferences}
                    </div>
                </div>
            </div>
        `;

        const externalCommentRow =
            (this.baseRequest.ApplicationForm.HasExternalCollaboratorsId === 1) // 1 = Yes
                ? `
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    External Collaborators Comment
                </div>
                <div class="col view-application-value-col">
                    ${this.ExternalCollaboratorsComment}
                </div>
            </div>
            `
            : "";

        let adminDetailsBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Admin Details
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Data and Biospecimen Sharing Plan
                    </div>
                    <div class="col view-application-value-col">
                        ${this.SharingPlan}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Ability to Use Biospecimens in Timely and Robust Manner
                    </div>
                    <div class="col view-application-value-col">
                        ${this.BiospecimenUsageAbility}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Anticipated Project Start Date
                    </div>
                    <div class="col view-application-value-col">
                        ${this.StartDate}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Anticipated Project Completion Date
                    </div>
                    <div class="col view-application-value-col">
                        ${this.CompletionDate}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        External Collaborators
                    </div>
                    <div class="col view-application-value-col">
                        ${this.HasExternalCollaborators}
                    </div>
                </div>
                ${externalCommentRow}
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Funding Status
                    </div>
                    <div class="col view-application-value-col">
                        ${this.FundingStatus}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Funding Sources
                    </div>
                    <div class="col view-application-value-col">
                        ${this.FundingSources}
                    </div>
                </div>
            </div>
        `;

        let requestHistoryBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Request History
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Previous Sample Request
                    </div>
                    <div class="col view-application-value-col">
                        ${this.HasPreviousRequest}
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        New or Resubmitted Request
                    </div>
                    <div class="col view-application-value-col">
                        ${this.NewOrResubmittedRequest}
                    </div>
                </div>
            </div>
        `;

        let engagementBlock = `
            <div class="view-application-container col-12">
                <div class="row view-application-header">
                    <div class="col">
                        Patient and/or Community Engagement Plans
                    </div>
                </div>
                <div class="row view-application-row">
                    <div class="col-auto view-application-field-col">
                        Patient and/or Community Engagement Plans
                    </div>
                    <div class="col view-application-value-col">
                        ${this.EngagementDescription}
                    </div>
                </div>
            </div>
        `;

        let biospecimenRequestBlock = `
            <div id="view-biospecimen-requests">

            </div>
        `;

        let html = `
            ${contactInformationBlock}
            <hr class="view-divider">
            ${letterOfSupportBlock}
            <hr class="view-divider">
            ${projectDetailsBlock}
            <hr class="view-divider">
            ${adminDetailsBlock}
            ${biospecimenRequestBlock}
            <hr class="view-divider">
            ${requestHistoryBlock}
            <hr class="view-divider">
            ${engagementBlock}
        `

        return html;
    }

    triggerPrint(): void {
        // Get HTML for PDF view
        let html = this.createPrintView();

        // Create iFrame element to store PDF
        let frame = document.createElement('iframe');

        // Removes Frame when print is complete
        let cleanup = () => {
            try {
                document.body.removeChild(frame);
            }
            catch {
                console.log("Error removing Frame from body");
            }
        };

        // when frame is loaded in browser
        frame.onload = () => {

            let frameWindow = frame.contentWindow;
            let doc = frame.contentDocument || frameWindow?.document;

            if (!frameWindow || !doc) {
                cleanup();
                return;
            }

            let frameTitle = this.createPdfFileName(this.baseRequest);
            try {
                doc.title = frameTitle;
            }
            catch {

            };

            let oldTitle = document.title;
            document.title = frameTitle;

            let restoreTitle = () => {
                document.title = oldTitle;
                cleanup();
            }

            if ('srcdoc' in frame) {
                (frame as any).srcdoc = html;
            }
            else {
                let frameWindow = frame.contentWindow;
                let doc = frame.contentDocument || frameWindow?.document;
                if (doc) {
                    doc.open();
                    doc.write(html);
                    doc.close();
                }
            }

            setTimeout(() => {
                try {
                    frameWindow.focus();
                    frameWindow.print();

                    if ('onafterprint' in frameWindow) {
                        frameWindow.onafterprint = restoreTitle;
                    }
                    else {
                        setTimeout(restoreTitle, 1500);
                    }
                }
                catch (e) {
                    restoreTitle();
                }
            }, 50);
        }

        // Add styling so frame is not shown in browser
        frame.style.position = 'fixed';
        frame.style.right = '0';
        frame.style.bottom = '0';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.border = '0';
        document.body.appendChild(frame);
    }

    private createPrintView(): string {
        let currentView = $(`#${this.viewId}`);
        let viewClone = currentView.clone(false, false);

        let headAssets = this.collectHeadAssetsHtml();
        let fileName = this.createPdfFileName(this.baseRequest);

        let title = "RECOVERCT Biospecimen Request"
        if (this.baseRequest.ApplicationForm.ProjectShortTitle) {
            title = this.baseRequest.ApplicationForm.ProjectShortTitle;
        }

        let printAdditionalHtml = `
            <div class="mb-4 pdf-title-bar">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="font-weight-600 font-size18">
                        ${title}
                    </div>
                    <div class="font-size14">
                        <span class="font-weight-600 pr-2">Last Updated: </span>${FormatDate(this.baseRequest.ApplicationForm.LastUpdated)}
                    </div>
                </div>
            </div>
        `;

        viewClone.prepend(printAdditionalHtml);
        const printCss = `
                .pdf-title-bar, .pdf-title-bar * {
                    color: #000000 !important;
                }
                body {
                    background: #fff;
                    padding: 36px;
                    font-family: "Segoe UI", Arial, sans-serif;
                    color: #0a2f6b;
                  }

                  .recoverct-application-view-container .view-application-header {
                    background: #143049 !important;
                    color: #ffffff !important;
                    border-bottom: 2px solid #0a2f6b !important;
                  }

                  .recoverct-application-view-container .view-application-header * {
                    color: #ffffff !important;
                  }

                  @media print {
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .recoverct-application-view-container .view-application-container {
                    break-inside: avoid;
                    page-break-inside: avoid;
                  }
                  .recoverct-application-view-container .view-application-row {
                    break-inside: avoid;
                    page-break-inside: avoid;
                  }
                  }
                `;

        let html = `
            <!doctype html>
            <html>
                <head>
                    <meta charset="utf-8">
                    <title>${fileName}</title> 
                    <base href="${document.baseURI}">
                    ${headAssets}
                    <style>${printCss}</style>
                </head>
                <body>${viewClone.prop('outerHTML')}</body>
            </html>
        `;
        return html;
    };

    private createPdfFileName(application: RequestFormExtended): string {
        let name = "Request";
        if (application.ApplicationForm.ProjectShortTitle) {
            name = application.ApplicationForm.ProjectShortTitle
        }
        let displayName = `RECOVERCT ${name}`;
        let formattedName = displayName.replace(/[\\/:*?"<>|]+/g, '')      // strip illegal filename chars
            .replace(/\s+/g, '_')
            .trim()
            .slice(0, 180);
        return formattedName;
    }

    private collectHeadAssetsHtml(): string {
        const pick = (sel: string) => Array.from(document.querySelectorAll(sel));

        // Everything already loaded on the main page
        const pageLinks = ([
            ...pick('link[rel~="stylesheet"]'),
            ...pick('link[rel="preload"][as="style"]')
        ] as HTMLLinkElement[]).map(l => l.href);

        // Unique + ordered
        const hrefs = Array.from(new Set([...pageLinks]));

        // Any inline <style> blocks on the page
        const inlineStyles = pick('style') as HTMLStyleElement[];


        return [
            ...hrefs.map(href => `<link rel="stylesheet" href="${href}">`),
            ...inlineStyles.map(s => s.outerHTML)

        ].join('\n');
    }
}

export class BiospecimenRequestView {
    viewId: string;
    isReviewer: boolean;
    baseRequest: ApplicationForm;
    biospecimenRequests: BiospecimenRequest[];
    userQueries: UserQuery[];

    constructor(viewId: string, isReviewer: boolean) {
        this.viewId = viewId;
        this.isReviewer = isReviewer;
    }

    initialize(baseRequest: RequestFormExtended, queryList: UserQuery[]) {
        this.baseRequest = baseRequest.ApplicationForm;
        this.biospecimenRequests = baseRequest.BiospecimenRequests;
        this.userQueries = queryList;
        this.generateHtml();
    }

    private generateHtml() {
        let content = "";
        for (let i = 0; i < this.biospecimenRequests.length; i++) {
            let req = this.biospecimenRequests[i];
            let requestTitle = `Biospecimen Request ${i + 1}`;

            if (this.baseRequest.ProjectShortTitle) {
                requestTitle = `${this.baseRequest.ProjectShortTitle} - Biospecimen Request ${i + 1}`;
            }

            let generalContent = this.createGeneralRows(req);
            let queryContent = this.createQueryRows(req);

            let reqContent = `
                <hr class="view-divider">
                <div class="view-application-container col-12">
                    <div class="row view-application-header">
                        <div class="col">
                            ${requestTitle}
                        </div>
                    </div>
                    ${generalContent}
                    ${queryContent}
                </div>
            `;
            content += reqContent;
        }
        $(`#${this.viewId}`).empty().html(content);
    }

    private createGeneralRows(request: BiospecimenRequest): string {
        let bioType = BiospecimenInventoryLookup.GetValueFromIdAndType(request.BiospecimenTypeId, "sample-type");
        let clinicalTrail = BiospecimenInventoryLookup.GetValueFromIdAndType(request.ClinicalTrialId, "trial");

        let displayCollVisit: string[] = [];
        let collWindowArr: string[] = request.VisitIds.split("|");
        collWindowArr.forEach(visitId => {
            let parsedWindow = parseInt(visitId);
            if (!isNaN(parsedWindow))  {
                let visit = VisitLookup.GetVisitMasterColumn(parsedWindow)
                displayCollVisit.push(visit);
            }
        })

        const volumeText = (request.Volume ?? "").toString().trim();

        const unitLabel = request.UnitId
            ? (BiospecimenInventoryLookup.GetValueFromIdAndType(request.UnitId, "unit-measure") ?? "")
            : "";
        const amountDisplay =
            volumeText && unitLabel ? `${volumeText} ${unitLabel}` :
                volumeText ? volumeText :
                    unitLabel ? unitLabel :
                        "�";

        let content = `
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Clinical Trial
                </div>
                <div class="col view-application-value-col">
                    ${clinicalTrail}
                </div>
            </div>
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Biospecimen Type
                </div>
                <div class="col view-application-value-col">
                    ${bioType}
                </div>
            </div>
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Collection Visits
                </div>
                <div class="col view-application-value-col">
                    ${displayCollVisit.join("<br>")}
                </div>
            </div>
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Biospecimens per Visit
                </div>
                <div class="col view-application-value-col">
                    ${request.Quantity.toString()}
                </div>
            </div>
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Amount per Biospecimen
                </div>
                <div class="col view-application-value-col">
                    ${amountDisplay}
                </div>
            </div>
        `;
        return content;
    }

    private createQueryRows(request: BiospecimenRequest): string {
        let queryRows = "";
        request.QueryIds.split("|").forEach(queryId => {
            let foundQuery = this.userQueries.find(query => query.Id.toString() == queryId);
            if (foundQuery) {
                let queryBtn = `
                    <div class="col-auto">
                        <a href="/secure/biospecimens/inventory-search/${foundQuery.Id}" target="_blank" class="text-decoration-none btn recoverct-blue-btn view-query-btn">
                            <i class="fa-solid fa-magnifying-glass pr-2"></i>View
                        </a>
                    </div>
                `;
                if (this.isReviewer) {
                    // potentially have something else for reviewers  
                }

                queryRows += `
                    <div class="card">
                        <div class="card-header">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="col">
                                    <div class="header-text d-flex align-items-center">
                                        <span class="expand-text d-block">${foundQuery.QueryName}</span>
                                    </div>
                                </div>
                                ${queryBtn}
                            </div>
                        </div>
                    </div>
                `
            }
        });

        let content = `
            <div class="row view-application-row">
                <div class="col-auto view-application-field-col">
                    Queries
                </div>
                <div class="col view-application-value-col">
                    <div class="query-view-gallery">
                        ${queryRows}
                    </div>
                </div>
            </div>
        `

        return content;
    }
}